/**
 * @file Shared HTTP route contracts for `lab`'s public surface under
 *       umbrella #358's URL-isomorphism design.
 * @module @labcraft/lab
 *
 * Route contracts define the wire protocol between client and server:
 * method, path, request schema, response schema. Handlers bind to these
 * contracts via `BackendService.bindRoute()`; the SPA imports the same
 * types via the `@labcraft/lab` alias so the frontend and the daemon
 * stay in lockstep.
 *
 * Path convention: routes are wire-true — each `.path` is the full
 * URL the daemon serves it at. Routes in this file are bound by
 * `Lab` directly; account-side routes are bound by the child
 * `AccountService` and live in `./account_routes.ts`
 * (`/api/account/*`).
 *
 * The surface is two HTTP routes plus one WebSocket. The combined
 * `GET` returns a discriminated `{kind: 'ok', ...} | {kind: 'conflict', ...}`
 * — `ok` carries `content + machines + expiresAt` for chapter render,
 * `conflict` carries `current` for the "engaged elsewhere" dialog. The
 * `POST` mutates the engagement. The WS lives in `service.ts` because
 * it's not a `defineRoute`-shaped HTTP route — its path is
 * `/api/machines/:machineId/terminals/:terminalId`, identifying the
 * running terminal (course/part/chapter are resolved server-side from
 * the cookie-authenticated learner's engagement).
 */

import { z } from 'zod'
import { defineRoute, CoreString } from '@labcraft/core'

// Re-export auth route contracts so the web app imports them via the
// same `@labcraft/lab` alias as the rest of the API.
export * from './account_routes.ts'

// Re-export the WebSocket envelope frame schemas so the frontend
// terminal client can import them via the same alias.
export * from './ws_messages.ts'

////////////////////////////////////////////////////////////////////////////////
// Shared building blocks
////////////////////////////////////////////////////////////////////////////////

/**
 * One machine in the chapter response's `machines` array. Structured
 * `{machineId, terminalId}` (#1054): the browser addresses the machine
 * by these author-chosen slots and builds the WS path
 * (`/api/machines/:machineId/terminals/:terminalId`) against its own
 * origin — lab no longer hands back a composed `wsUrl` the client had
 * to reverse-parse to recover `terminalId`. Lab resolves `machineId` →
 * the internal content-keyed `vmId` in one authenticated place (the
 * terminal upgrade handler, off the engagement record). The hypervisor
 * is never browser-reachable.
 *
 * @property machineId - Stable, descriptive id from the Part's
 *   `MachineSpec.id` (`'shell'`, `'client'`, `'server'`).
 * @property terminalId - Terminal slot within the machine. v1 supports
 *   one serial-console pty per VM, so this is `'shell'`; multi-shell
 *   expansion returns one entry per terminal.
 */
export const MachineSchema = z.object({
  machineId: z.string().min(1),
  terminalId: z.string().min(1),
})

/** Parsed `Machine`. */
export type Machine = z.infer<typeof MachineSchema>

/**
 * Summary of the learner's current engagement, returned inside the
 * `kind === 'conflict'` branch of the combined chapter response when
 * the requested chapter URL doesn't match where the learner is
 * engaged. Feeds the "Continue / Start fresh" dialog on the SPA —
 * includes display titles (`partTitle`, `chapterTitle`) so the
 * dialog renders human-readable copy without a second fetch.
 *
 * @property courseId - Current engagement's course id.
 * @property courseTitle - Display title of the current course.
 * @property partId - Current engagement's part id.
 * @property partTitle - Display title of the current part.
 * @property chapterId - Current engagement's chapter id.
 * @property chapterTitle - Display title of the current chapter.
 * @property chapterUrl - Canonical SPA path
 *   (`/courses/:courseId/:partId/:chapterId`) the "Continue where
 *   you were" button navigates to.
 * @property machineCount - Number of machines in the current
 *   engagement's recipe. Surfaced as "N machines running" in the
 *   dialog copy.
 * @property startedAt - Unix ms when the engagement was first
 *   created (NOT updated on switchTo). Drives the "started X minutes
 *   ago" line.
 */
export const EngagementSummarySchema = z.object({
  courseId: z.string().min(1),
  courseTitle: z.string().min(1),
  partId: z.string().min(1),
  partTitle: z.string().min(1),
  chapterId: z.string().min(1),
  chapterTitle: z.string().min(1),
  chapterUrl: z.string().min(1),
  machineCount: z.number().int().nonnegative(),
  startedAt: z.number().int().positive(),
})

/** Parsed `EngagementSummary`. */
export type EngagementSummary = z.infer<typeof EngagementSummarySchema>

////////////////////////////////////////////////////////////////////////////////
// Content shape — what the SPA renders the chapter view from
////////////////////////////////////////////////////////////////////////////////

/**
 * Course-scoped metadata the SPA renders the chrome from.
 *
 * @property id - Course id (URL slug).
 * @property title - Display title.
 * @property tagline - Course tagline.
 * @property glossary - Course-scoped glossary entries (id, label,
 *   short, body, optional seeAlso). Schema is `z.unknown()` here —
 *   the source of truth is `@labcraft/course-kit`'s `Term` type, and
 *   re-declaring it as a Zod schema in the wire layer would force a
 *   second source of truth that drifts. The SPA imports `Term` from
 *   course-kit and asserts at the boundary.
 */
export const CourseHeaderSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  tagline: z.string().min(1),
  glossary: z.array(z.unknown()),
})

/** Parsed `CourseHeader`. */
export type CourseHeader = z.infer<typeof CourseHeaderSchema>

/**
 * Minimal chapter listing — id, 0-indexed number within its Part
 * (matches the chapter's `NN-` directory prefix; the opening is `0`),
 * title. Used inside `PartListing.chapters` for nav surfaces.
 */
export const ChapterListingSchema = z.object({
  id: z.string().min(1),
  number: z.number().int().nonnegative(),
  title: z.string().min(1),
})

/** Parsed `ChapterListing`. */
export type ChapterListing = z.infer<typeof ChapterListingSchema>

/**
 * A Part with chapter listings for navigation. No `module` function
 * (those are SPA-side lazy imports today; #368 swaps to backend-
 * served compiled bodies).
 */
export const PartListingSchema = z.object({
  id: z.string().min(1),
  number: z.number().int().nonnegative(),
  tier: z.enum(['free', 'paid']),
  title: z.string().min(1),
  tagline: z.string().nullable(),
  prereqs: z.array(z.string().min(1)),
  chapters: z.array(ChapterListingSchema),
})

/** Parsed `PartListing`. */
export type PartListing = z.infer<typeof PartListingSchema>

/**
 * A planned-but-unbuilt Part — a course-graph node the course owns
 * (`status: 'designed'`) that has no authored content yet. The course
 * landing renders these as `Upcoming` rows (name only, non-navigable).
 * Deliberately minimal: a stable key + display title, nothing more —
 * there is no `number` / `tier` / chapter count to show for a Part that
 * doesn't exist yet.
 *
 * @property id - the owning course-graph node id (`<course>/<slug>`);
 *   used as a stable React key, not a navigation target
 * @property title - the Part's display name (the graph node's label)
 */
export const UpcomingPartSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
})

/** Parsed `UpcomingPart`. */
export type UpcomingPart = z.infer<typeof UpcomingPartSchema>

/**
 * Pointer to a specific chapter URL — `{courseId, partId, chapterId}`.
 * The wire-shape successor to `ChapterRef` in course-kit (which
 * still uses the legacy `chapterSlug` name until Part E renames it).
 */
export const ChapterRefSchema = z.object({
  courseId: z.string().min(1),
  partId: z.string().min(1),
  chapterId: z.string().min(1),
})

/** Parsed `ChapterRef`. */
export type ChapterRef = z.infer<typeof ChapterRefSchema>

/**
 * Content-hashed pointer to a chapter's compiled MDX module. The
 * `url` is the route the SPA dynamic-imports at render time
 * (`/api/courses/:courseId/:partId/:chapterId/module-:hash.js`); the
 * `hash` is the same value embedded in the URL, exposed separately so
 * a curious SPA debug surface can render it without parsing the URL.
 *
 * @property url - SPA dynamic-`import()` target
 * @property hash - 8-char content hash; matches the URL segment
 */
export const ChapterModuleSchema = z.object({
  url: z.string().min(1),
  hash: z.string().regex(/^[0-9a-f]{8}$/),
})

/** Parsed `ChapterModule`. */
export type ChapterModule = z.infer<typeof ChapterModuleSchema>

/**
 * The chapter being rendered, with its full `ChapterSpec`. The spec
 * shape is `z.unknown()` because the source of truth is
 * course-kit's `ChapterSpec` and re-declaring it here would force
 * two schemas in lockstep. SPA imports the type from course-kit and
 * asserts at the boundary.
 */
export const ChapterPayloadSchema = z.object({
  courseId: z.string().min(1),
  partId: z.string().min(1),
  chapterId: z.string().min(1),
  number: z.number().int().nonnegative(),
  spec: z.unknown(),
  module: ChapterModuleSchema,
})

/** Parsed `ChapterPayload`. */
export type ChapterPayload = z.infer<typeof ChapterPayloadSchema>

/** A course-qualified graph node id (`<course>/<slug>`) on the wire. */
const NodeIdSchema = z.string().min(1)

/**
 * One node of the serialized course-graph region. Mirrors
 * `@labcraft/course-kit`'s `GraphNode` field-for-field; the SPA casts the
 * parsed value to the branded `CourseGraph` at the boundary (the brand is
 * structural-only, so no runtime check is lost).
 *
 * @property id - global `<course>/<slug>` identifier
 * @property label - human-readable display label
 * @property status - build status (`shipped` / `designed` / `placeholder`)
 * @property course - owning course slug (the `<course>` segment of `id`)
 * @property skills - optional skill-name metadata
 */
const GraphNodeSchema = z.object({
  id: NodeIdSchema,
  label: z.string(),
  status: z.enum(['shipped', 'designed', 'placeholder']),
  course: z.string(),
  skills: z.array(z.string()).optional(),
})

/**
 * One directed edge of the serialized course-graph region. Discriminated
 * on `kind`; only `requiresFor` carries a `scope`. Mirrors
 * `@labcraft/course-kit`'s `GraphEdge` union.
 *
 * @property kind - relationship (`requires` / `adjacent` / `requiresFor`)
 * @property from - prerequisite / source node id (arrow tail)
 * @property to - dependent / target node id (arrow head)
 * @property scope - `requiresFor` only: the named sub-path of `to`
 */
const GraphEdgeSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('requires'),
    from: NodeIdSchema,
    to: NodeIdSchema,
  }),
  z.object({
    kind: z.literal('adjacent'),
    from: NodeIdSchema,
    to: NodeIdSchema,
  }),
  z.object({
    kind: z.literal('requiresFor'),
    from: NodeIdSchema,
    to: NodeIdSchema,
    scope: z.string(),
  }),
])

/**
 * The serialized `CourseGraph` region — one course's nodes + edges as the
 * combined chapter endpoint carries it (umbrella #1357 Part C). The shape
 * is closed and function-free, so it validates concretely at the boundary
 * (unlike the `z.unknown()` seams `spec` / `glossary` use, whose source of
 * truth is a course-kit type). The SPA casts the parsed value to the
 * branded `CourseGraph` from `@labcraft/course-kit` exactly as it casts
 * `spec` / `glossary` today. Reused verbatim by the Part-assessment fetch
 * route — never forked.
 *
 * @property course - the owning course slug
 * @property nodes - the region's node set
 * @property edges - the region's edge set
 */
export const CourseGraphWireSchema = z.object({
  course: z.string(),
  nodes: z.array(GraphNodeSchema),
  edges: z.array(GraphEdgeSchema),
})

/** Parsed `CourseGraphWire`. */
export type CourseGraphWire = z.infer<typeof CourseGraphWireSchema>

/**
 * The `content` portion of the combined endpoint payload. Everything
 * the SPA needs to render a chapter view without statically
 * importing a course package on the client.
 *
 * **Body deliberately omitted in v1.** The SPA continues to
 * lazy-import each chapter's MDX module statically. Umbrella #368
 * adds `chapter.body` as an additive field once the MDX-build
 * pipeline ships; the rest of this shape stays stable across that
 * swap.
 *
 * @property course - Course-scoped header data + glossary.
 * @property parts - Every Part of the course, with chapter listings
 *   for navigation (course landing, breadcrumbs, prev/next part).
 * @property chapter - The specific chapter being rendered: id,
 *   number-in-part, and the `ChapterSpec` (eyebrow, title, subtitle,
 *   whyYouCare, steps).
 * @property neighbors - Prev/next chapter pointers within the
 *   course's flat chapter sequence, or `null` at the ends. Drives
 *   `<ChapterFooter/>`.
 * @property graph - The course's composed dependency-graph region
 *   (umbrella #1357 Part C), threaded to the reader for the assessment
 *   phase's interleaved-draw policy. Additive + backward-compatible:
 *   absent on older manifests / courses without a graph region, in
 *   which case the reader falls back to an empty region and
 *   interleaving never fires.
 */
export const ChapterContentSchema = z.object({
  course: CourseHeaderSchema,
  parts: z.array(PartListingSchema),
  chapter: ChapterPayloadSchema,
  neighbors: z.object({
    prev: ChapterRefSchema.nullable(),
    next: ChapterRefSchema.nullable(),
    // Umbrella #1446 Part B — when the current chapter is the last
    // `lesson` of a role-tagged Part that carries a assessment, the
    // forward target is the Part assessment (`/courses/:c/:p/assessment`),
    // not the next chapter. The SPA footer routes there instead of `next`.
    // Absent/false on role-less Parts (pre-conformance) and on non-last
    // lessons — the flat chapter→chapter flow is unchanged there.
    nextIsAssessment: z.boolean().optional(),
  }),
  graph: CourseGraphWireSchema.optional(),
})

/** Parsed `ChapterContent`. */
export type ChapterContent = z.infer<typeof ChapterContentSchema>

////////////////////////////////////////////////////////////////////////////////
// Routes
////////////////////////////////////////////////////////////////////////////////

/**
 * Combined chapter response — discriminated by `kind`. The single
 * endpoint that a chapter page hits on mount; collapses what would
 * otherwise be a content fetch + runtime fetch + presence-poll triad.
 *
 *   - `kind: 'ok'` — the URL matches the learner's current engagement
 *     (or the learner had no engagement and one was created at this
 *     URL). Carries `content` for rendering, `machines` for terminal
 *     attach, `expiresAt` for the sliding TTL, `partPassed` — the
 *     learner's terminal assessment-pass state for THIS chapter's owning
 *     Part (from `learner_part_assessment`, umbrella #1357), the same
 *     value for every chapter within a Part — and `completedObjectives`,
 *     the ids of this chapter's already-completed objectives so the reader
 *     rehydrates its checkmarks on resume / reload (#1493).
 *   - `kind: 'conflict'` — the learner has an engagement at a
 *     different URL. Carries only `current` (the engaged-elsewhere
 *     summary, with display titles) — no chapter content, because the
 *     SPA renders the dialog instead of the chapter view.
 *
 * The combined endpoint always returns HTTP 200; the conflict case is
 * data, not an error.
 */
export const ChapterResponseSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('ok'),
    content: ChapterContentSchema,
    machines: z.array(MachineSchema),
    expiresAt: z.number().int().positive(),
    partPassed: z.boolean(),
    completedObjectives: z.array(z.string()),
  }),
  z.object({
    kind: z.literal('conflict'),
    current: EngagementSummarySchema,
  }),
])

/** Parsed `ChapterResponse`. */
export type ChapterResponse = z.infer<typeof ChapterResponseSchema>

/**
 * `GET /api/courses/:courseId/:partId/:chapterId` — the combined
 * chapter endpoint. URL-isomorphic with the SPA's chapter URL
 * (`/courses/:courseId/:partId/:chapterId`) plus the `/api` prefix.
 *
 * Semantics:
 *   - 401 if unauthenticated.
 *   - 404 if the (course, part, chapter) tuple doesn't resolve.
 *   - 200 with `kind: 'ok'` if the learner has no engagement yet, or
 *     has one at exactly this chapter URL. find-or-create runs; on
 *     `'created'`, machines are booted in the background before the
 *     response is composed.
 *   - 200 with `kind: 'conflict'` if the learner has an engagement at
 *     a different URL — the existing engagement is untouched; the SPA
 *     renders the dialog and offers Continue or Start-fresh.
 *
 * Idempotent on the `ok` path. Two-tab races on first navigation
 * collapse via `Sessions.findOrCreate`'s Lua atomicity.
 */
export const GetChapterRoute = defineRoute({
  method: 'GET',
  path: '/api/courses/:courseId/:partId/:chapterId',
  response: ChapterResponseSchema,
  description:
    'Combined chapter endpoint — content + machines on ok, summary on conflict.',
})

////////////////////////////////////////////////////////////////////////////////
// Part-boundary CAT assessment (umbrella #1357 Part D + E)
////////////////////////////////////////////////////////////////////////////////

/**
 * One chapter of a Part as the Part-assessment fetch route carries it
 * (umbrella #1357). The lean shape the fail-screen's remediation surface
 * resolves a weak concept back to its owning chapter from — no MDX module,
 * no full `ChapterSpec`.
 *
 * @property chapterId - the chapter's URL slug (the remediation link
 *   target)
 * @property title - the chapter's display title (the link label)
 * @property concept - the chapter's taught course-graph node, or `null`
 *   for an objectives-only chapter; used to map a weak concept back to its
 *   owning chapter when the assessment has no authored remediation override
 * @property objectiveIds - the chapter's objective ids, surfaced on the
 *   remediation link
 * @property role - the chapter's Part-flow role (`opening | lesson |
 *   closing`, umbrella #1446), or absent on a role-less (pre-conformance)
 *   chapter. The assessment page reads it to route the learner to the
 *   `closing` chapter after a pass (the assessment → closing transition).
 */
export const PartAssessmentChapterSchema = z.object({
  chapterId: z.string().min(1),
  title: z.string().min(1),
  concept: z.string().nullable(),
  objectiveIds: z.array(z.string()),
  role: z.enum(['opening', 'lesson', 'closing']).optional(),
})

/** Parsed `PartAssessmentChapter`. */
export type PartAssessmentChapter = z.infer<typeof PartAssessmentChapterSchema>

/**
 * `GET /api/courses/:courseId/:partId/assessment` response — everything
 * the Part-assessment page needs to drive the Part-boundary CAT + resolve
 * remediation on a fail (umbrella #1357 Part D + E).
 *
 * `assessment` is `z.unknown()` because the source of truth is
 * course-kit's `PartAssessment` type — re-declaring it as a Zod schema
 * here would force a second source of truth that drifts (same reasoning as
 * `spec` / `glossary`). The SPA casts it to `PartAssessment` and rehydrates
 * the bank's `RegExp` `accept` entries via `QuizSerde.rehydratePartAssessment`
 * at the boundary. `graph` reuses `CourseGraphWireSchema` verbatim — the
 * SAME serialized graph shape the chapter content carries, never forked.
 *
 * @property courseId - URL course slug
 * @property partId - URL part slug
 * @property assessment - the Part's consolidated CAT bank (serialized wire
 *   form; SPA rehydrates)
 * @property graph - the course's composed dependency-graph region for the
 *   draw policy, or absent when the manifest carries no graph region
 * @property assessmentPassed - the learner's Part-level pass state (from
 *   `learner_part_assessment`), lifting the retry gate on a revisit of an
 *   already-passed assessment
 * @property chapters - the Part's chapters in the lean remediation shape
 */
export const PartAssessmentResponseSchema = z.object({
  courseId: z.string().min(1),
  partId: z.string().min(1),
  assessment: z.unknown(),
  graph: CourseGraphWireSchema.optional(),
  assessmentPassed: z.boolean(),
  chapters: z.array(PartAssessmentChapterSchema),
})

/** Parsed `PartAssessmentResponse`. */
export type PartAssessmentResponse = z.infer<
  typeof PartAssessmentResponseSchema
>

/**
 * `GET /api/courses/:courseId/:partId/assessment` — the Part-boundary CAT
 * assessment endpoint (umbrella #1357). URL-isomorphic with the SPA's
 * Part-assessment URL (`/courses/:courseId/:partId/assessment`) plus the
 * `/api` prefix. Returns the consolidated assessment bank, the course
 * graph region, the learner's Part-level pass state, and the Part's
 * chapters for remediation resolution.
 *
 * The static `assessment` segment shares the shape of the dynamic chapter
 * endpoint (`.../:chapterId`); the core router matches pattern routes in
 * registration order, so `Lab` binds this route BEFORE the chapter route
 * — a real chapter id never equals the literal `assessment` (reserved,
 * lint-enforced by Part G/B), so the chapter endpoint still resolves every
 * other id.
 *
 *   - 401 if unauthenticated.
 *   - 404 if the (course, part) tuple doesn't resolve or the Part ships no
 *     assessment.
 *   - 200 with the assessment payload otherwise.
 */
export const GetPartAssessmentRoute = defineRoute({
  method: 'GET',
  path: '/api/courses/:courseId/:partId/assessment',
  response: PartAssessmentResponseSchema,
  description:
    'Part-boundary CAT assessment endpoint — bank + graph + pass state + chapters.',
})

////////////////////////////////////////////////////////////////////////////////
// GET /api/progress/:courseId — auth-scoped per-Part pass state (#1446 Part B)
////////////////////////////////////////////////////////////////////////////////

/** Path params for `GET /api/progress/:courseId`. */
export const ProgressParamsSchema = z.object({
  courseId: z.string().min(1),
})

/** Parsed `ProgressParams`. */
export type ProgressParams = z.infer<typeof ProgressParamsSchema>

/**
 * `GET /api/progress/:courseId` response — the learner's per-Part pass
 * state for one course (umbrella #1446 Part B).
 *
 * This is the **auth-scoped** pass-state surface the course landing joins
 * client-side into {@link PartListing} to derive `done`. It is deliberately
 * a separate endpoint from the **public** `GET /api/courses/:courseId`
 * (`handleCourseGet`, no auth per #368 single-tenant v1): keeping per-learner
 * state off the public response means no learner-specific field is ever
 * served unauthenticated. The source of truth is the `learner_part_assessment`
 * table (via `passedPartIds`), NOT the `progress_part_assessment_pass` Umami
 * event.
 *
 * @property passedPartIds - the ids of every Part in the course whose
 *   assessment (Part assessment) the learner has passed; empty for a
 *   learner with no passes yet
 */
export const ProgressResponseSchema = z.object({
  passedPartIds: z.array(z.string().min(1)),
})

/** Parsed `ProgressResponse`. */
export type ProgressResponse = z.infer<typeof ProgressResponseSchema>

/**
 * `GET /api/progress/:courseId` — the authenticated learner's per-Part
 * pass state for one course (umbrella #1446 Part B). 401 without a lab
 * session cookie; 200 with `{ passedPartIds }` otherwise (empty array for
 * a learner with no passes, or an unknown course — the endpoint reads the
 * pass table, not the course registry, so it never 404s).
 */
export const GetProgressRoute = defineRoute({
  method: 'GET',
  path: '/api/progress/:courseId',
  params: ProgressParamsSchema,
  response: ProgressResponseSchema,
  description: CoreString.oneLine`
    The authenticated learner's per-Part assessment pass state for one course.
    Auth-scoped; the course landing joins it to derive Done.
  `,
})

////////////////////////////////////////////////////////////////////////////////
// POST /api/courses/:courseId/:partId/feedback — closing feedback (#1446 Part E)
////////////////////////////////////////////////////////////////////////////////

/** Path params for `POST /api/courses/:courseId/:partId/feedback`. */
export const PartFeedbackParamsSchema = z.object({
  courseId: z.string().min(1),
  partId: z.string().min(1),
})

/** Parsed `PartFeedbackParams`. */
export type PartFeedbackParams = z.infer<typeof PartFeedbackParamsSchema>

/**
 * Body of `POST /api/courses/:courseId/:partId/feedback` — a learner's
 * closing feedback for a Part (umbrella #1446 Part E). Every field is
 * optional individually, but `.refine` requires at least one to be present
 * so an empty submit is a 400. The per-Part prompts (`clear` / `useful` /
 * `struggled`) and the general `experience` note are free text; `rating`
 * is an optional 1–5 overall score.
 *
 * @property rating - optional 1–5 overall rating
 * @property clear - optional "what was clear" note
 * @property useful - optional "what was useful" note
 * @property struggled - optional "where you struggled" note
 * @property experience - optional general-experience note
 */
export const PartFeedbackRequestSchema = z
  .object({
    rating: z.number().int().min(1).max(5).optional(),
    clear: z.string().min(1).max(2000).optional(),
    useful: z.string().min(1).max(2000).optional(),
    struggled: z.string().min(1).max(2000).optional(),
    experience: z.string().min(1).max(2000).optional(),
  })
  .refine(
    body =>
      body.rating !== undefined ||
      body.clear !== undefined ||
      body.useful !== undefined ||
      body.struggled !== undefined ||
      body.experience !== undefined,
    { message: 'feedback must carry at least a rating or one comment' },
  )

/** Parsed `PartFeedbackRequest`. */
export type PartFeedbackRequest = z.infer<typeof PartFeedbackRequestSchema>

/**
 * `POST /api/courses/:courseId/:partId/feedback` — persist the learner's
 * closing feedback for a Part (umbrella #1446 Part E). Auth-scoped to the
 * learner + Part; upserts one `learner_part_feedback` row and emits the
 * mentor/recalibration signal (#1417). 401 without a lab session cookie;
 * 400 on an empty body; 204 on success.
 */
export const PostPartFeedbackRoute = defineRoute({
  method: 'POST',
  path: '/api/courses/:courseId/:partId/feedback',
  params: PartFeedbackParamsSchema,
  request: PartFeedbackRequestSchema,
  response: z.void(),
  description: CoreString.oneLine`
    Persist the learner's closing feedback for a Part (rating and/or comments)
    and emit the recalibration signal. Auth-scoped; 204 on success.
  `,
})

/**
 * Body of `POST /api/courses/engagement`. The SPA sends the chapter
 * URL it wants to switch to; the server parses it into
 * `(courseId, partId, chapterId)`, recipe-diffs vs the current
 * engagement, lifecycles machines, and persists the new record.
 *
 * @property chapterUrl - Path-only canonical SPA URL:
 *   `/courses/:courseId/:partId/:chapterId`. Server validates the
 *   shape and resolves the tuple; the SPA doesn't need to break it
 *   into parts itself.
 */
export const EngagementRequestSchema = z.object({
  chapterUrl: z
    .string()
    .regex(
      /^\/courses\/[^/]+\/[^/]+\/[^/]+$/,
      'must match /courses/:courseId/:partId/:chapterId',
    ),
})

/** Parsed `EngagementRequest`. */
export type EngagementRequest = z.infer<typeof EngagementRequestSchema>

/**
 * Response of `POST /api/courses/engagement`. Echoes the new
 * location and the resulting machine list. The SPA navigates to the
 * URL (it knew this already) and attaches WSes to the returned
 * machines.
 *
 * @property location - The new engagement location.
 * @property machines - Machines in the post-switch recipe, in stable
 *   order. The SPA dials each `wsUrl` to attach a terminal.
 * @property expiresAt - Sliding-TTL expiry as of this call.
 */
export const EngagementResponseSchema = z.object({
  location: ChapterRefSchema,
  machines: z.array(MachineSchema),
  expiresAt: z.number().int().positive(),
})

/** Parsed `EngagementResponse`. */
export type EngagementResponse = z.infer<typeof EngagementResponseSchema>

// Operator catalog writes (install / uninstall / enable / refresh) are
// intentionally NOT part of lab's surface. `@labcraft/admin` owns the
// `installed_courses` write path — it holds the platform schema and
// operator authn — and its writes fire the `lc_courses` NOTIFY that
// lab's `Courses` registry LISTENs for (see the migration
// `20260619000005_installed_courses.sql`). Lab reads the catalog for
// its own registry but exposes no mutation route; the admin-facing
// contracts live in `@labcraft/admin`'s `courses_routes.ts`.

////////////////////////////////////////////////////////////////////////////////
// Catalog + per-course routes (umbrella #368 Part B)
////////////////////////////////////////////////////////////////////////////////

/**
 * Catalog entry — what `GET /api/courses` returns per installed
 * course. Lean by design: id + display title + tagline, no per-part
 * payload. The SPA's course-list landing renders from this; the
 * per-course endpoint carries the part listing.
 */
export const CourseCatalogEntrySchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  tagline: z.string().min(1),
})

/** Parsed `CourseCatalogEntry`. */
export type CourseCatalogEntry = z.infer<typeof CourseCatalogEntrySchema>

/**
 * `GET /api/courses` — catalog listing. Public, unauthenticated; the
 * catalog itself is not learner-scoped (single-tenant in #368 v1).
 * Future per-tenant scoping (post-#368) layers an auth check + filter
 * here without changing the wire shape.
 */
export const GetCoursesRoute = defineRoute({
  method: 'GET',
  path: '/api/courses',
  response: z.array(CourseCatalogEntrySchema),
  description: 'List installed + enabled courses (catalog).',
})

/**
 * `GET /api/courses/:courseId` — per-course metadata. Returns the
 * course header (id, title, tagline, glossary) + the part listing
 * (same shape `ChapterContent.parts` carries) without a specific
 * `chapter` slice, plus `upcomingParts` — the course's planned-but-
 * unbuilt Parts (own-course `designed` graph nodes), so the landing
 * can show the full Part path with `Upcoming` rows. The SPA's
 * course-landing renders from this.
 */
export const GetCourseRoute = defineRoute({
  method: 'GET',
  path: '/api/courses/:courseId',
  response: z.object({
    course: CourseHeaderSchema,
    parts: z.array(PartListingSchema),
    upcomingParts: z.array(UpcomingPartSchema),
  }),
  description: 'Per-course metadata + part listing + upcoming Parts.',
})

/**
 * `GET /api/courses/:courseId/:partId/:chapterId/module/:hash` —
 * serve the compiled MDX module bytes for one chapter.
 *
 * Response is `Content-Type: application/javascript` + immutable
 * cache headers (`max-age=31536000, immutable`) — safe because the
 * URL is content-addressed by `:hash`. Mismatched hash returns 404
 * so the SPA refetches the chapter content (which carries the fresh
 * hash) and retries with the new URL.
 *
 * No `.js` extension on the URL — browsers honor `Content-Type` for
 * native ESM imports; the extension is what `extname()` would split
 * out of an intra-segment literal (`module-:hash.js`), which the
 * core HTTP router's segment-by-segment matcher doesn't support.
 * `module/:hash` is two clean segments.
 *
 * The `response: z.string()` schema is the wire-text view — the
 * runtime serves raw bytes via a `Response(Buffer, ...)` construction
 * the schema doesn't validate. Lab's per-route response validation
 * (when enabled) is skipped for this route.
 */
export const GetChapterModuleRoute = defineRoute({
  method: 'GET',
  path: '/api/courses/:courseId/:partId/:chapterId/module/:hash',
  response: z.string(),
  description: 'Serve a content-hashed compiled MDX module.',
})

/**
 * `POST /api/courses/engagement` — engagement mutation. Body is
 * `{ chapterUrl }`. Server:
 *   1. Authenticates the cookie.
 *   2. Rate-limits per learner (`@labcraft/redis`'s `rate_limit.ts`)
 *      to bound abuse.
 *   3. Parses `chapterUrl` → `(courseId, partId, chapterId)`; 404 if
 *      the tuple doesn't resolve.
 *   4. Reads the current engagement record.
 *   5. Diffs the new recipe vs the prior `machineIds`: boots new,
 *      keeps matching (content-keyed vmIds persist), shuts down
 *      removed.
 *   6. Persists the new record (last-writer-wins).
 *   7. Responds with the new location + machine list.
 *
 * 429 on rate-limit exhaustion; 404 if the URL doesn't resolve; 401
 * if unauthenticated.
 */
export const PostEngagementRoute = defineRoute({
  method: 'POST',
  path: '/api/courses/engagement',
  request: EngagementRequestSchema,
  response: EngagementResponseSchema,
  description:
    'Switch the learner engagement to a new chapter URL; recipe-diff machines.',
})

/**
 * Query for `DELETE /api/courses/engagement` — how the release treats the
 * engagement's VMs (umbrella #1572).
 *
 * @property intent - `'suspend'` leaves the VMs to the hypervisor's 2-minute
 *   disconnect-grace snapshot (resumable), for the involuntary tab-close /
 *   reload / off-site-nav path. `'terminate'` — the default when absent —
 *   shuts them down promptly, for the deliberate "done" leaves (a Part's
 *   closing chapter, the "Back to course" boundary). Absent ⇒ terminate, so
 *   the behaviour is backward-compatible for any caller that omits it.
 */
export const DeleteEngagementQuerySchema = z.object({
  intent: z.enum(['suspend', 'terminate']).optional(),
})

/**
 * `DELETE /api/courses/engagement` — release the learner's engagement
 * (umbrella #1446 Part C, #1572). Always deletes the `session:{learnerId}`
 * record; the `intent` query decides the VMs' fate. `intent=suspend` (the
 * involuntary tab-close / reload path) leaves them to the hypervisor's
 * 2-minute disconnect-grace snapshot, so the learner resumes where they left
 * off; `intent=terminate` (the default — the deliberate "done" leaves: a
 * Part's closing chapter, "Back to course") shuts them down promptly. Either
 * way the next engagement is a clean `created`, never a phantom conflict.
 * Idempotent: a 204 whether or not a record existed. Requires a lab session
 * cookie; 401 without. A transient terminal-socket drop is NOT a leave (the
 * SPA silent-reconnects it), so only an explicit leave reaches here.
 */
export const DeleteEngagementRoute = defineRoute({
  method: 'DELETE',
  path: '/api/courses/engagement',
  query: DeleteEngagementQuerySchema,
  response: z.void(),
  description: CoreString.oneLine`
    Release the learner's engagement — delete the record and either suspend
    (intent=suspend) or shut down (default) its machines. Idempotent (204).
  `,
})

////////////////////////////////////////////////////////////////////////////////
// POST /api/machines/:machineId/suspend — voluntary VM suspend
////////////////////////////////////////////////////////////////////////////////

/** Path params for `POST /api/machines/:machineId/suspend`. */
export const SuspendMachineParamsSchema = z.object({
  machineId: z.string().min(1),
})

/** Parsed `SuspendMachineParams`. */
export type SuspendMachineParams = z.infer<typeof SuspendMachineParamsSchema>

/**
 * `POST /api/machines/:machineId/suspend` — learner-triggered "Pause"
 * affordance. Requires a lab session cookie; `:machineId` is the
 * browser-facing id, which the handler translates to the caller's own
 * content-keyed vmId before proxying to the hypervisor's
 * voluntary-suspend route (which snapshots immediately, bypassing the
 * post-detach grace period). 401 without auth; 404 when the caller
 * has no engagement or the machine isn't in the active recipe;
 * otherwise idempotent.
 */
export const PostSuspendMachineRoute = defineRoute({
  method: 'POST',
  path: '/api/machines/:machineId/suspend',
  params: SuspendMachineParamsSchema,
  response: z.void(),
  description: CoreString.oneLine`
    Learner-triggered voluntary suspend of the caller's own machine. Requires a
    session cookie; 404 when the machine isn't in the caller's active recipe;
    idempotent on already-suspended machines.
  `,
})

////////////////////////////////////////////////////////////////////////////////
// POST /api/machines/:machineId/restart — terminate + clear a failed VM (#1009)
////////////////////////////////////////////////////////////////////////////////

/** Path params for `POST /api/machines/:machineId/restart`. */
export const RestartMachineParamsSchema = z.object({
  machineId: z.string().min(1),
})

/** Parsed `RestartMachineParams`. */
export type RestartMachineParams = z.infer<typeof RestartMachineParamsSchema>

/**
 * `POST /api/machines/:machineId/restart` — the "start a fresh shell"
 * recovery for a terminally-`failed` machine (#1009). Requires a lab
 * session cookie; `:machineId` is the browser-facing id, translated to
 * the caller's own content-keyed vmId before proxying to the
 * hypervisor's `shutdown`, which terminates the wedged VM and deletes
 * its session so the next WS-open cold-boots a clean VM under the same
 * (deterministic) id. 401 without auth; 404 when the caller has no
 * engagement or the machine isn't in the active recipe; otherwise
 * idempotent on already-gone machines.
 */
export const PostRestartMachineRoute = defineRoute({
  method: 'POST',
  path: '/api/machines/:machineId/restart',
  params: RestartMachineParamsSchema,
  response: z.void(),
  description: CoreString.oneLine`
    "Start a fresh shell" recovery for a failed machine (#1009), scoped to the
    caller's own machines via the session cookie. Terminates the VM via
    hypervisor shutdown so the next open cold-boots a clean one. 404 when the
    machine isn't in the caller's active recipe; idempotent when already gone.
  `,
})

////////////////////////////////////////////////////////////////////////////////
// POST /api/engagement/touch — extend the engagement TTL
////////////////////////////////////////////////////////////////////////////////

/**
 * Response of `POST /api/engagement/touch`.
 *
 * @property expiresAt - New sliding-TTL deadline (ms since epoch).
 */
export const TouchEngagementResponseSchema = z.object({
  expiresAt: z.number().int().positive(),
})

/** Parsed `TouchEngagementResponse`. */
export type TouchEngagementResponse = z.infer<
  typeof TouchEngagementResponseSchema
>

/**
 * `POST /api/engagement/touch` — refresh the sliding engagement TTL.
 * Wired to the SPA's "Keep alive" button inside the pre-expiry
 * warning banner. Returns the new deadline so the SPA can update its
 * local cache without a round-trip through the WS lifecycle path.
 */
export const PostEngagementTouchRoute = defineRoute({
  method: 'POST',
  path: '/api/engagement/touch',
  response: TouchEngagementResponseSchema,
  description: CoreString.oneLine`
    Refresh the learner's engagement sliding TTL. Returns the new deadline (ms
    since epoch).
  `,
})

////////////////////////////////////////////////////////////////////////////////
// Runtime config (SPA bootstrap)
////////////////////////////////////////////////////////////////////////////////

/**
 * Runtime config the lab SPA fetches once on mount. Carries the values
 * the static bundle can't bake at build time.
 *
 * @property analytics - Self-hosted Umami tracker config for the lab
 *   surface, or `null` when analytics is off. `scriptUrl` + `websiteId`
 *   are operator-set in the admin lab settings panel and read fresh per
 *   request, so the tracker activates on the next page load with no
 *   rebuild. The website ID is distinct from www's — learner-app
 *   pageviews report to a separate Umami website. The SPA injects the
 *   tracker only when this is non-null (see `@labcraft/ui`'s
 *   `useAnalytics`).
 */
export const LabConfigResponseSchema = z.object({
  analytics: z
    .object({
      scriptUrl: z.string(),
      websiteId: z.string(),
    })
    .nullable(),
})

/** Parsed `LabConfigResponse`. */
export type LabConfigResponse = z.infer<typeof LabConfigResponseSchema>

/**
 * `GET /api/config` — runtime config for the lab SPA bootstrap. Read
 * fresh per request (the Settings cache is kept live by the
 * `lc_settings` `pg_notify` trigger), so an admin analytics flip
 * reflects on the next page load with no rebuild and no restart.
 */
export const ConfigRoute = defineRoute({
  method: 'GET',
  path: '/api/config',
  response: LabConfigResponseSchema,
  description: 'Runtime config (analytics) for the lab SPA.',
})

////////////////////////////////////////////////////////////////////////////////
// In-lab issue report (#1672)
////////////////////////////////////////////////////////////////////////////////

/** Broad category a learner can optionally tag an issue report with. */
export const ReportCategorySchema = z.enum([
  'answer-should-pass',
  'confusing-instructions',
  'terminal-vm',
  'other',
])

/** Parsed report category. */
export type ReportCategory = z.infer<typeof ReportCategorySchema>

/**
 * Human labels for each category — the single source of truth shared by
 * the report modal (button text) and the lab service (email subject +
 * body), so the wire id and the operator-facing label can't drift.
 */
export const REPORT_CATEGORY_LABELS: Record<ReportCategory, string> = {
  'answer-should-pass': 'Answer should have passed',
  'confusing-instructions': 'Instructions are confusing',
  'terminal-vm': 'Terminal / VM problem',
  other: 'Other',
}

/**
 * Client-assembled "recent lab context" the learner opts to attach — the
 * bits the SPA already holds (reader location, active objective, the
 * command lines it received over the terminal WS, cached VM state,
 * chapter content hash). Every field is optional: the SPA sends only what
 * the live session exposes. The lab service secret-redacts the free-form
 * parts (command lines) before forwarding — nothing here is trusted as
 * already-safe.
 *
 * @property where - Human "where you are" label, e.g. "bash · Ch 3 / 12".
 * @property courseId - Course of the current engagement.
 * @property partId - Part within the course.
 * @property chapterId - Chapter within the part.
 * @property objectiveId - Active objective/checker id at report time.
 * @property checkerOutcome - Last checker outcome for that objective.
 * @property contentHash - Compiled chapter-module content hash.
 * @property vmState - Cached VM lifecycle state at report time.
 * @property vmReason - Reason for the last VM lifecycle transition.
 * @property commands - Recent command lines (newest last) with exit codes
 *   (`null` when unknown); redacted server-side before it leaves the daemon.
 */
export const ReportContextSchema = z.object({
  where: z.string().max(200).optional(),
  courseId: z.string().max(120).optional(),
  partId: z.string().max(120).optional(),
  chapterId: z.string().max(120).optional(),
  objectiveId: z.string().max(120).optional(),
  checkerOutcome: z.string().max(120).optional(),
  contentHash: z.string().max(64).optional(),
  vmState: z.string().max(40).optional(),
  vmReason: z.string().max(40).optional(),
  commands: z
    .array(
      z.object({ cmd: z.string().max(500), exit: z.number().int().nullable() }),
    )
    .max(20)
    .optional(),
})

/** Parsed report context. */
export type ReportContext = z.infer<typeof ReportContextSchema>

/**
 * Browser/client particulars attached to every report (#1672) — useful
 * for UI bugs, which can happen on any screen, not just the reader. So
 * unlike the reader `context` (gated behind the attach-lab-context
 * toggle), this rides along regardless. The lab service redacts `url`
 * server-side (a query string can carry a token); the rest are low-
 * sensitivity descriptors.
 *
 * @property userAgent - `navigator.userAgent`.
 * @property language - `navigator.language`.
 * @property platform - `navigator.platform`.
 * @property viewport - Inner viewport as `<w>x<h>`.
 * @property url - The page URL at report time (redacted server-side).
 */
export const ReportClientSchema = z.object({
  userAgent: z.string().max(500).optional(),
  language: z.string().max(40).optional(),
  platform: z.string().max(120).optional(),
  viewport: z.string().max(40).optional(),
  url: z.string().max(500).optional(),
})

/** Parsed report client particulars. */
export type ReportClient = z.infer<typeof ReportClientSchema>

/**
 * Body of `POST /api/report` — the thin submission the report modal
 * sends. The learner's own words + an optional category + the
 * attach-context gate, plus the client-assembled reader `context`
 * (present only when `attachContext` is true, i.e. in a reader with a
 * terminal) and the always-present browser `client` particulars.
 *
 * @property category - Broad category, or `null` when the learner picked
 *   none.
 * @property message - The learner's free-text description (required).
 * @property attachContext - Whether the learner opted to attach recent
 *   lab context (only offered in a reader with a terminal).
 * @property context - The attached reader bundle; present iff
 *   `attachContext`.
 * @property client - Browser/client particulars; attached on every report.
 */
export const ReportRequestSchema = z.object({
  category: ReportCategorySchema.nullable(),
  message: z.string().min(1).max(4000),
  attachContext: z.boolean(),
  client: ReportClientSchema.optional(),
  context: ReportContextSchema.optional(),
})

/** Parsed `ReportRequest`. */
export type ReportRequest = z.infer<typeof ReportRequestSchema>

/** Response of `POST /api/report`. */
export const ReportResponseSchema = z.object({ status: z.literal('accepted') })

/** Parsed `ReportResponse`. */
export type ReportResponse = z.infer<typeof ReportResponseSchema>

/**
 * `POST /api/report` — submit an in-lab learner issue report (#1672).
 * Auth-scoped to the learner session; per-learner rate-limited. The
 * handler secret-redacts the message + command tail, then forwards the
 * report to the operator inbox via the Email client (email-only — no
 * persisted record). 401 without a session; 429 when rate-limited; 200
 * `{ status: 'accepted' }` once the report is enqueued for delivery.
 */
export const PostReportRoute = defineRoute({
  method: 'POST',
  path: '/api/report',
  request: ReportRequestSchema,
  response: ReportResponseSchema,
  description: CoreString.oneLine`
    Submit an in-lab learner issue report; redacted + emailed to the
    operator inbox. Auth-scoped, rate-limited; 200 on accept.
  `,
})

////////////////////////////////////////////////////////////////////////////////
// Web Push — capacity-queue "your spot is ready" (#1257 Part E)
////////////////////////////////////////////////////////////////////////////////

/**
 * A browser `PushSubscription` as sent to the daemon (umbrella #1257 Part E,
 * #1284) — the same shape `JSON.stringify(subscription)` produces after
 * `registration.pushManager.subscribe(...)`. Structurally compatible with
 * `web-push`'s `PushSubscription`.
 *
 * @property endpoint - The push service endpoint URL for this subscription.
 * @property expirationTime - Optional expiry (epoch ms) or `null`.
 * @property keys - The client's ECDH public key (`p256dh`) + `auth` secret.
 */
export const PushSubscriptionSchema = z.object({
  endpoint: z.string().url(),
  expirationTime: z.number().nullable().optional(),
  keys: z.object({
    p256dh: z.string().min(1),
    auth: z.string().min(1),
  }),
})

/** Parsed `PushSubscriptionSchema`. */
export type PushSubscriptionInput = z.infer<typeof PushSubscriptionSchema>

/** Response of `POST /api/push/subscribe`. */
export const PushSubscribeResponseSchema = z.object({ ok: z.literal(true) })

/** Parsed `PushSubscribeResponse`. */
export type PushSubscribeResponse = z.infer<typeof PushSubscribeResponseSchema>

/**
 * `POST /api/push/subscribe` — store the caller's Web Push subscription so the
 * capacity queue can push "your spot is ready" when it admits them (umbrella
 * #1257 Part E, #1284). Keyed to the cookie-authenticated learner server-side.
 */
export const PushSubscribeRoute = defineRoute({
  method: 'POST',
  path: '/api/push/subscribe',
  request: PushSubscriptionSchema,
  response: PushSubscribeResponseSchema,
  description: CoreString.oneLine`
    Store the caller's Web Push subscription (keyed to the authenticated
    learner) so the capacity queue can notify them when their lab slot opens.
  `,
})

/** Response of `GET /api/push/vapid`. */
export const PushVapidResponseSchema = z.object({ publicKey: z.string() })

/** Parsed `PushVapidResponse`. */
export type PushVapidResponse = z.infer<typeof PushVapidResponseSchema>

/**
 * `GET /api/push/vapid` — the VAPID public key the SPA needs to call
 * `pushManager.subscribe` (umbrella #1257 Part E, #1284). An empty string means
 * push is dark (no keypair provisioned); the SPA then skips the opt-in.
 */
export const PushVapidRoute = defineRoute({
  method: 'GET',
  path: '/api/push/vapid',
  response: PushVapidResponseSchema,
  description: 'The VAPID public key for Web Push subscription (empty = dark).',
})

/** Response of `POST /api/push/resume-reminder`. */
export const ResumeReminderResponseSchema = z.object({ ok: z.literal(true) })

/** Parsed `ResumeReminderResponse`. */
export type ResumeReminderResponse = z.infer<
  typeof ResumeReminderResponseSchema
>

/**
 * `POST /api/push/resume-reminder` — the free learner opts in, from the
 * out-of-time wall, to a Web Push when their daily lab budget resets (the next
 * UTC-day boundary). Keyed to the cookie-authenticated learner. The SPA calls
 * this only after {@link PushSubscribeRoute} has stored a subscription for the
 * push to reach, so a learner with the flag but no subscription is a harmless
 * no-op at sweep time. Requestless — the opt-in carries no payload beyond the
 * session identity.
 */
export const ResumeReminderRoute = defineRoute({
  method: 'POST',
  path: '/api/push/resume-reminder',
  response: ResumeReminderResponseSchema,
  description: CoreString.oneLine`
    Opt the authenticated learner into a Web Push when their daily free lab
    budget next resets (the UTC-day boundary).
  `,
})
